import {
  Box,
  Flex,
  IconCart,
  Link,
  Text,
  useBreakpointValue,
} from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { usePaymentMethod } from 'src/contexto/hooks/usePaymentMethod'
import { theme } from 'src/styles/themes/default'
import { ProductCartData } from 'src/types/Carrinho'
import { formatarPreco } from 'src/util/formatar'
import {
  arePixOrBilletAvailable,
  getInCashAvailablePaymentMethods,
  isCreditCardAvailable,
} from 'src/util/payment'
import { getStyles } from './styles'
import { Ad } from 'src/services/marketplace-api/dtos'

export type FixedBarProps = {
  setCartProductData: Dispatch<SetStateAction<ProductCartData | undefined>>
  adActive: Ad
  enableScroll?: boolean
  variationTitle?: string
}

export default function FixedBarProduct({
  setCartProductData,
  adActive: adActive,
  enableScroll = false,
  variationTitle,
}: FixedBarProps) {
  const isSmallScreen = useBreakpointValue({ base: false, lg: true })
  const { payment } = usePaymentMethod()
  const route = useRouter()

  const [scroll, setScroll] = useState(enableScroll)

  const hasPixOrBoleto = arePixOrBilletAvailable(payment)
  const hasCreditCard = isCreditCardAvailable(payment)
  const installment = adActive?.preco.parcelamento
  const billet = adActive?.formas_pagamento.boleto
  const hasStock = adActive && adActive.estoque > 0

  const scrollDispositivo = isSmallScreen ? 640 : 850

  const styles = getStyles(scroll, adActive?.estoque)

  const moveForItems = [
    { action: 'description', description: 'Informações do Produto' },
    { action: 'datasheet', description: 'Informações adicionais' },
    { action: 'related', description: 'Produtos Relacionados' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY

      if (currentPosition > scrollDispositivo) return setScroll(true)

      setScroll(false)
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isSmallScreen, scrollDispositivo])

  const moveFor = (posicao: string): void => {
    let coordinates: DOMRect | null = null

    const key = {
      description: document.getElementById('product-information'),
      datasheet: document.getElementById('product-technical-sheet'),
      related: document.getElementById('carousel-related-products'),
    }

    const el: HTMLElement | null = key[posicao]

    if (el) coordinates = el.getBoundingClientRect()

    if (coordinates) {
      try {
        window.scrollTo(
          coordinates.left + window.scrollX,
          coordinates.top + window.scrollY - 250
        )
      } catch (err) {
        console.error('Não foi possível mover a tela')
      }
    }
  }

  const handlePress = (e: MouseEvent<HTMLAnchorElement>, adId: number) => {
    e.preventDefault()
    setCartProductData({
      anuncio_id: adId,
      quantidade: 1,
      url: `${route.asPath}`,
    })
  }

  const formatTitle = (title: string) =>
    title.replace('- Sem Cor', '').replace('- Sem Voltagem', '')

  return (
    <Box
      bg={theme.colors.white}
      bottom={['0', 'unset']}
      boxShadow={styles.container.boxShadow}
      className={styles.container.className}
      display={styles.container.display}
      height="5.5rem"
      opacity={styles.container.opacity}
      pointerEvents={styles.container.pointerEvents}
      position="fixed"
      top={['unset', '0']}
      transform={styles.container.transform}
      transition="all 0.3s"
      width="100%"
      zIndex="1000"
    >
      <Flex
        align="center"
        h="100%"
        w="100%"
        justifyContent="space-between"
        maxW="81.875rem"
        mx="auto"
        px={{ base: '0.5rem', sm: '0.9375rem' }}
      >
        <Flex display={['none', 'none', 'flex']} w={styles.titleContent.width}>
          {variationTitle && (
            <Text
              color={theme.colors.grey[100]}
              fontSize={{ base: '1rem', lg: '1.125rem' }}
              fontWeight="600"
              lineHeight={{ base: '1.5rem', lg: '2.25rem' }}
              margin={0}
              noOfLines={1}
            >
              {formatTitle(variationTitle)}
            </Text>
          )}
        </Flex>

        {hasStock ? (
          <Flex
            flexDir="column"
            flex="1"
            justifyContent={styles.priceContent.justifyContent}
            px={{ base: '0.5rem', md: '1rem' }}
            minW="0"
          >
            {hasCreditCard && (
              <Text
                fontSize={['0.75rem', '1rem']}
                color="neutral.600"
                display={['block', 'none']}
                mb="0.0625rem"
              >
                em{' '}
                <Text as="strong" fontSize={['0.75rem', '1rem']}>
                  {installment.quantidade}x
                </Text>{' '}
                de{' '}
                <Text as="strong" fontSize={['0.75rem', '1rem']}>
                  {formatarPreco(installment.valor)}
                </Text>{' '}
              </Text>
            )}
            {hasPixOrBoleto && (
              <Box>
                {hasCreditCard && (
                  <Text
                    as="span"
                    fontSize={['0.75rem', '1rem']}
                    display={['inline-block', 'none']}
                  >
                    ou&nbsp;
                  </Text>
                )}
                <Text
                  color={theme.primary.main}
                  fontSize={['0.875rem', '1rem', '1.5rem']}
                  fontWeight="bold"
                  mb="0"
                  display={['inline-block', 'block']}
                  noOfLines={1}
                >
                  {formatarPreco(billet.por)}
                </Text>
                <Text
                  color={theme.colors.grey[300]}
                  fontSize={['0.625rem', '0.75rem', '1rem']}
                  fontWeight="400"
                  margin={0}
                  noOfLines={1}
                >
                  {`à vista no ${getInCashAvailablePaymentMethods(payment)}`}
                </Text>
              </Box>
            )}
          </Flex>
        ) : (
          <Flex
            flex="1"
            justifyContent="center"
            px={{ base: '0.5rem', md: '1rem' }}
          >
            <Text
              color={theme.colors.grey[100]}
              fontSize={{ base: '1rem', lg: '1.125rem' }}
              fontWeight="700"
              textAlign="center"
              noOfLines={1}
            >
              Produto indisponível
            </Text>
          </Flex>
        )}

        {hasStock && (
          <Flex
            justifyContent={['center', 'flex-end']}
            flex={{ base: '0 0 auto', md: '1' }}
          >
            <Link
              _hover={{ bgColor: '#60a00e' }}
              alignItems="center"
              as="button"
              bg={theme.success.main}
              borderRadius="0.375rem"
              color={theme.colors.white}
              display="flex"
              fontSize={{ base: '0.8rem', sm: '0.9375rem' }}
              fontWeight="600"
              h="2.5rem"
              justifyContent="center"
              letterSpacing="-0.0067rem"
              onClick={(e) => handlePress(e, adActive.id)}
              w={{ base: '8rem', sm: '11.8125rem', lg: '18.4375rem' }}
              minW={{ base: '8rem', sm: '11.8125rem' }}
              px={{ base: '0.5rem', sm: '1rem' }}
            >
              <IconCart
                color={theme.colors.white}
                height={{ base: '1rem', sm: '1.25rem' }}
                marginRight={{ base: '0.25rem', sm: '0.5rem' }}
                width={{ base: '1rem', sm: '1.25rem' }}
              />
              <Text as="span" noOfLines={1}>
                Comprar
              </Text>
            </Link>
          </Flex>
        )}
      </Flex>

      <Flex
        alignItems="center"
        bgColor="#f6f6f6"
        display={['none', 'none', 'flex']}
        justifyContent="space-between"
      >
        <Flex
          alignItems="center"
          height="2.5rem"
          justifyContent="space-between"
          maxW="81.875rem"
          mx="auto"
          px={{ base: '0.5rem', sm: '0.9375rem' }}
          width="100%"
        >
          {moveForItems.map((item) => (
            <Box
              _hover={{ color: theme.primary.main }}
              cursor="pointer"
              key={item.action}
              onClick={() => moveFor(item.action)}
              fontSize={{ base: '0.8rem', lg: '1rem' }}
              textAlign="center"
              px="0.5rem"
              noOfLines={1}
            >
              {item.description}
            </Box>
          ))}
        </Flex>
      </Flex>
    </Box>
  )
}
